import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import './styles/theme.css';
import './styles/scopes.css';
import './styles/installations.css';
import './styles/dashboard.css';
import './styles/permissions.css';
import './styles/collaboration.css';
import { AppLayout } from './layout/AppLayout';
import { PortalProvider } from './context/PortalContext';
import { LanguageProvider } from './i18n/LanguageContext';
import { MarketingProvider } from './marketing/MarketingContext';
import Account from './routes/Account';
import Activate from './routes/Activate';
import ClientAccounts from './routes/ClientAccounts';
import SupportDesk from './routes/SupportDesk';
import Dashboard from './routes/Dashboard';
import Docs from './routes/Docs';
import MarketingAdmin from './routes/MarketingAdmin';
import Collaboration from './routes/Collaboration';
import CollaborationPartnerProfile from './routes/CollaborationPartnerProfile';
import CapaRegister from './routes/CapaRegister';
import Products from './routes/Products';
import Instances from './routes/Instances';
import Permissions from './routes/Permissions';
import RiskReview from './routes/RiskReview';
import Support from './routes/Support';
import TestAdmin from './routes/TestAdmin';
import EvidenceAdmin from './routes/EvidenceAdmin';
import SplashConfigAdmin from './routes/SplashConfigAdmin';
import AiTextEnhancementAdmin from './routes/AiTextEnhancementAdmin';
import PiiDetectionAdmin from './routes/PiiDetectionAdmin';
import SpfxReleasesAdmin from './routes/SpfxReleasesAdmin';
import ScopesAdmin from './routes/ScopesAdmin';
import SupplierTermsAdmin from './routes/SupplierTermsAdmin';
import TemplateLibrary from './routes/TemplateLibrary';
import StaffRoleAdmin from './routes/StaffRoleAdmin';
import CommunicationCompose from './routes/CommunicationCompose';

const App = (): JSX.Element => (
  <PortalProvider>
    <LanguageProvider>
    <FluentProvider theme={webLightTheme}>
      <BrowserRouter>
        <MarketingProvider>
          <AppLayout>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/products" element={<Products />} />
              <Route path="/docs" element={<Docs />} />
              <Route path="/template-library" element={<TemplateLibrary />} />
              <Route path="/support" element={<Support />} />
              <Route path="/account" element={<Account />} />
              <Route path="/communication/compose" element={<CommunicationCompose />} />
              <Route path="/activate" element={<Activate />} />
              <Route path="/collaboration" element={<Collaboration />} />
              <Route path="/collaboration/partners/:partnerId" element={<CollaborationPartnerProfile />} />
              <Route path="/capa-register" element={<CapaRegister />} />
              <Route path="/instances" element={<Instances />} />
              <Route path="/risk" element={<RiskReview />} />
              <Route path="/permissions" element={<Permissions />} />
              <Route path="/client-accounts" element={<ClientAccounts />} />
              <Route path="/support-desk" element={<SupportDesk />} />
              <Route path="/portal/admin/staff-roles" element={<StaffRoleAdmin />} />
              <Route path="/portal/admin/marketing" element={<MarketingAdmin />} />
              <Route path="/portal/admin/ai-text" element={<AiTextEnhancementAdmin />} />
              <Route path="/portal/admin/pii-detection" element={<PiiDetectionAdmin />} />
              <Route path="/portal/admin/spfx-releases" element={<SpfxReleasesAdmin />} />
              <Route path="/portal/admin/scopes" element={<ScopesAdmin />} />
              <Route path="/portal/admin/supplier-terms" element={<SupplierTermsAdmin />} />
              <Route path="/portal/admin/splash" element={<SplashConfigAdmin />} />
              <Route path="/portal/admin/evidence" element={<EvidenceAdmin />} />
              <Route path="/portal/admin/tests" element={<TestAdmin />} />
              <Route path="*" element={<div className="card">Not found</div>} />
            </Routes>
          </AppLayout>
        </MarketingProvider>
      </BrowserRouter>
    </FluentProvider>
    </LanguageProvider>
  </PortalProvider>
);

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(<App />);
}
